import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowRightLeft, CheckCircle2, FileText, Info, Loader2, Mail, Pencil, Plus, RefreshCw, Save, Search, UserCheck, XCircle } from "lucide-react";
import {
  ORDER_LEGACY_TERMS,
  PLAN_LABELS,
  adminOrderCreateSchema,
  formatTry,
  guestRangeLabel,
  trDativeSuffix,
  type OrderRow,
  type OrderStatus,
  type OrderWeddingOption,
} from "@wedding/shared";
import { adminOrdersApi, adminPlansApi, adminUsersApi } from "../lib/api";
import { cn } from "../lib/utils";

/**
 * "Satış & Site → Online satış → Siparişler" (F8b). Ödeme havale/EFT ile gelir.
 *
 * Hak modeli (2026-10-14): "Ödendi" hesabın planını DEĞİŞTİRMEZ — siparişin
 * hakkı (sipariş anındaki limitler, misafir = aralığın üst sınırı) seçilen düğüne ya da
 * alıcının bir sonraki düğününe bağlanır; "Taşı" ile değiştirilebilir. İptal →
 * hak düşer. Alıcı e-postası isteğe bağlı, durumu kartta; "E-postayı gönder"
 * ile sonradan yollanır. Silme yok (fatura kaydı).
 */

const FILTERS: (OrderStatus | "all")[] = ["pending", "paid", "cancelled", "all"];

const STATUS_STYLE: Record<OrderStatus, string> = {
  pending: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  paid: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  cancelled: "bg-muted text-muted-foreground",
};

/**
 * Siparişin misafir etiketi: hak kopyalandıysa SATILAN sayı ("300’e kadar") —
 * kademe sınırları sonradan değişse de sipariş kendi sayısını gösterir;
 * eski (hak kopyasız) siparişte bugünkü sınırlarla aralık.
 */
function orderRangeLabel(t: (key: string, opts?: Record<string, unknown>) => string, order: OrderRow, bounds: readonly number[]): string {
  const n = order.ent_max_guests;
  return typeof n === "number" ? t("landing.contact.rangeUpTo", { n, suffix: trDativeSuffix(n) }) : guestRangeLabel(t, bounds, order.guest_range);
}

export function OrdersPanel({ bounds, showToast }: { bounds: readonly number[]; showToast: (msg: string, ok?: boolean) => void }) {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState<OrderStatus | "all">("pending");
  const [items, setItems] = useState<OrderRow[] | null>(null);
  const [available, setAvailable] = useState(true);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (cursor?: string) => {
      setLoading(true);
      try {
        const res = await adminOrdersApi.list({ status: filter === "all" ? undefined : filter, cursor });
        const data = res.data ?? { available: true, items: [], nextCursor: null };
        setAvailable(data.available);
        setItems((prev) => (cursor && prev ? [...prev, ...data.items] : data.items));
        setNext(data.nextCursor);
      } catch (err) {
        showToast(t("siteAdmin.orders.loadFailed", { error: err instanceof Error ? err.message : String(err) }), false);
        setItems((prev) => prev ?? []);
      } finally {
        setLoading(false);
      }
    },
    [filter, showToast, t],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const replace = (o: OrderRow) => setItems((prev) => (prev ?? []).map((x) => (x.id === o.id ? o : x)));
  const reload = useCallback(() => void load(), [load]);
  const [showManual, setShowManual] = useState(false);

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="orders-panel">
      <div className="px-5 py-4 border-b border-border bg-muted/30 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">{t("siteAdmin.orders.title")}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{t("siteAdmin.orders.desc")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
              className={cn(
                "rounded-full px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer",
                filter === f ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:text-foreground",
              )}
            >
              {t(`siteAdmin.orders.filter.${f}`)}
            </button>
          ))}
          <button
            type="button"
            onClick={() => void load()}
            aria-label={t("siteAdmin.reload")}
            className="p-2 rounded-full border border-border text-muted-foreground hover:text-foreground cursor-pointer"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setShowManual((v) => !v)}
            aria-expanded={showManual}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 cursor-pointer"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{t("siteAdmin.orders.manualNew")}</span>
          </button>
        </div>
      </div>
      {showManual && (
        <ManualOrderForm
          showToast={showToast}
          onCreated={() => {
            setShowManual(false);
            setFilter("pending");
            void load();
          }}
        />
      )}

      <div className="px-5 py-4 space-y-3">
        <div data-testid="orders-email-info" className="rounded-xl border border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground space-y-1">
          <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            <Mail className="h-4 w-4 text-primary" aria-hidden="true" />
            <span>{t("siteAdmin.orders.emailInfoTitle")}</span>
          </p>
          <p>1. {t("siteAdmin.orders.emailInfo1")}</p>
          <p>2. {t("siteAdmin.orders.emailInfo2")}</p>
          <p>3. {t("siteAdmin.orders.emailInfo3")}</p>
        </div>
        {!available ? (
          <p className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-800 dark:text-amber-300" data-testid="orders-unavailable">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
            {t("siteAdmin.orders.unavailable")}
          </p>
        ) : items === null ? (
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("siteAdmin.orders.empty")}</p>
        ) : (
          items.map((o) => (
            <OrderCard key={o.id} order={o} bounds={bounds} lang={i18n.language} onChange={replace} onReload={reload} showToast={showToast} />
          ))
        )}
        {next && (
          <button
            type="button"
            onClick={() => void load(next)}
            disabled={loading}
            className="w-full rounded-full border border-border py-2.5 text-sm hover:bg-primary/5 cursor-pointer disabled:opacity-50"
          >
            {loading ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : t("siteAdmin.orders.more")}
          </button>
        )}
      </div>
    </div>
  );
}

const fmtBytes = (b: number, lang: string) => {
  const nf = new Intl.NumberFormat(lang, { maximumFractionDigits: 1 });
  return b >= 1024 ** 3 ? `${nf.format(b / 1024 ** 3)} GB` : `${nf.format(b / 1024 ** 2)} MB`;
};

/** Sunucu hata kodu → anlaşılır metin. */
const ERROR_KEYS: Record<string, string> = {
  order_conflict: "siteAdmin.orders.conflict",
  orders_migration_required: "siteAdmin.orders.migrationRequired",
  wedding_not_owned: "siteAdmin.orders.weddingNotOwned",
  wedding_not_found: "siteAdmin.orders.weddingNotFound",
  not_pending: "siteAdmin.orders.notPending",
  // Finansal denetim (2026-10-16)
  payment_ref_required: "siteAdmin.orders.errors.paymentRef",
  reason_required: "siteAdmin.orders.errors.reason",
  package_in_use: "siteAdmin.orders.errors.packageInUse",
  target_has_package: "siteAdmin.orders.errors.targetHasPackage",
  package_not_found: "siteAdmin.orders.errors.packageNotFound",
  package_not_sellable: "siteAdmin.orders.errors.packageNotSellable",
  invalid_transition: "siteAdmin.orders.errors.invalidTransition",
};

/** Siparişin paket adı: tanımdaki ad (özel/kurumsal dahil); yoksa site paketi adı. */
const packageLabel = (o: OrderRow) => o.package_label ?? PLAN_LABELS[o.plan] ?? o.plan;

function OrderCard({ order, bounds, lang, onChange, onReload, showToast }: {
  bounds: readonly number[];
  order: OrderRow;
  lang: string;
  onChange: (o: OrderRow) => void;
  onReload: () => void;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<"paid" | "cancelled" | "notes" | "move" | "email" | null>(null);
  const [paymentNote, setPaymentNote] = useState(order.payment_note ?? "");
  const [adminNote, setAdminNote] = useState(order.admin_note ?? "");
  /** Açık pencere: "Ödendi" (düğün + e-posta seçimi) ya da "Taşı". */
  const [mode, setMode] = useState<"paid" | "move" | null>(null);
  const [weddings, setWeddings] = useState<OrderWeddingOption[] | null>(null);
  const [choice, setChoice] = useState(""); // "" → bir sonraki düğün
  const [send, setSend] = useState(true);
  /** Alıcıya bağlı olmayan düğün (gelin aldı, damat açtı) — adres/slug ile. */
  const [foreignSlug, setForeignSlug] = useState("");
  /** Ödenmiş paketi taşıma gerekçesi (sunucu zorunlu tutar; deftere yazılır). */
  const [moveReason, setMoveReason] = useState("");
  const [editing, setEditing] = useState(false);
  /** Defterdeki sözleşme kopyası (alıcının onayladığı metin + özet) — açılınca yüklenir. */
  const [contract, setContract] = useState<
    | { state: "loading" }
    | { state: "ready"; available: boolean; documents: { text: string; hash: string; at: string } | null; events: number }
    | null
  >(null);
  const toggleContract = async () => {
    if (contract) {
      setContract(null);
      return;
    }
    setContract({ state: "loading" });
    try {
      const res = await adminOrdersApi.ledger(order.id);
      setContract({ state: "ready", available: res.data?.available ?? false, documents: res.data?.documents ?? null, events: res.data?.events.length ?? 0 });
    } catch (err) {
      setContract(null);
      showToast(t("siteAdmin.ledger.loadFailed", { error: err instanceof Error ? err.message : String(err) }), false);
    }
  };
  const [fixName, setFixName] = useState(order.name);
  const [fixEmail, setFixEmail] = useState(order.email);
  const notesDirty = paymentNote !== (order.payment_note ?? "") || adminNote !== (order.admin_note ?? "");
  const fail = (err: unknown) => {
    const msg = err instanceof Error ? err.message : String(err);
    if (ERROR_KEYS[msg]) {
      showToast(t(ERROR_KEYS[msg]), false);
      if (msg === "order_conflict") onReload();
      return;
    }
    showToast(t("siteAdmin.saveFailed", { error: msg }), false);
  };

  const open = async (next: "paid" | "move") => {
    setMode(next);
    setWeddings(null);
    setSend(true);
    setMoveReason("");
    try {
      const res = await adminOrdersApi.weddings(order.id);
      const list = res.data?.weddings ?? [];
      setWeddings(list);
      // Etkinlik tarihi tutan düğün varsa o; yoksa "bir sonraki düğün" — yanlış düğüne bağlamaktansa.
      const match = list.find((w) => w.date_time?.startsWith(order.event_date));
      setChoice(next === "move" ? (order.wedding_id ?? "") : (match?.id ?? ""));
    } catch (err) {
      setWeddings([]);
      showToast(t("siteAdmin.orders.weddingsFailed", { error: err instanceof Error ? err.message : String(err) }), false);
    }
  };

  /** Tutarı olan siparişte "Ödendi" için ödeme referansı zorunlu (dekont / havale açıklaması). */
  const needsPaymentRef = order.amount_try > 0 && !paymentNote.trim();
  const confirmPaid = async () => {
    if (needsPaymentRef) {
      showToast(t("siteAdmin.orders.errors.paymentRef"), false);
      return;
    }
    setBusy("paid");
    try {
      const res = await adminOrdersApi.patch(order.id, {
        status: "paid",
        ...(foreignSlug.trim() ? { applyToSlug: foreignSlug.trim() } : { applyTo: choice || null }),
        sendEmail: send,
        ...(notesDirty ? { paymentNote, adminNote } : {}),
      });
      if (res.data?.order) onChange(res.data.order);
      const d = res.data;
      showToast(t(d?.organizerCreated ? "siteAdmin.orders.paidCreated" : "siteAdmin.orders.paidExisting"));
      if (d?.emailSent === false) showToast(t("siteAdmin.orders.paidNoEmail"), false);
      else if (!send) showToast(t("siteAdmin.orders.paidEmailSkipped"), false);
      if (d?.deactivated) showToast(t("siteAdmin.orders.deactivated"), false);
      setMode(null);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const confirmMove = async () => {
    if (moveReason.trim().length < 3) {
      showToast(t("siteAdmin.orders.errors.reason"), false);
      return;
    }
    setBusy("move");
    try {
      const res = await adminOrdersApi.patch(order.id, {
        ...(foreignSlug.trim() ? { applyToSlug: foreignSlug.trim() } : { applyTo: choice || null }),
        reason: moveReason.trim(),
      });
      if (res.data?.order) onChange(res.data.order);
      showToast(t("siteAdmin.orders.moved"));
      setMode(null);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    let text = t(order.status === "paid" ? "siteAdmin.orders.cancelPaidConfirm" : "siteAdmin.orders.cancelConfirm", { number: order.number });
    // Ödenmiş + düğüne bağlı: iptalin düğüne etkisi sayılarla (düğün günü yüklemeler durmasın).
    if (order.status === "paid" && order.wedding_id) {
      try {
        const im = (await adminOrdersApi.impact(order.id)).data;
        if (im?.wedding) {
          text += "\n\n" + t("siteAdmin.orders.impact", {
            wedding: im.wedding.title,
            guests: im.usage.guests,
            media: im.usage.media,
            guestsAfter: im.after.guests,
            mediaAfter: im.after.media,
          });
          if (im.overAfter) text += "\n" + t("siteAdmin.orders.impactOver");
        }
      } catch {
        /* önizleme alınamazsa genel uyarı yeterli */
      }
    }
    // Ödenmiş siparişi iptal = iade → gerekçe zorunlu (deftere + audit'e yazılır).
    let reason: string | undefined;
    if (order.status === "paid") {
      const answer = window.prompt(`${text}\n\n${t("siteAdmin.orders.refundReasonPrompt")}`);
      if (answer === null) return;
      if (answer.trim().length < 3) {
        showToast(t("siteAdmin.orders.errors.reason"), false);
        return;
      }
      reason = answer.trim();
    } else if (!window.confirm(text)) return;
    setBusy("cancelled");
    try {
      const res = await adminOrdersApi.patch(order.id, {
        status: "cancelled",
        ...(reason ? { reason } : {}),
        ...(notesDirty ? { paymentNote, adminNote } : {}),
      });
      if (res.data?.order) onChange(res.data.order);
      showToast(t("siteAdmin.orders.cancelled"));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const sendEmail = async () => {
    setBusy("email");
    try {
      const res = await adminOrdersApi.patch(order.id, { resendEmail: true });
      if (res.data?.order) onChange(res.data.order);
      if (res.data?.emailSent) showToast(t("siteAdmin.orders.emailSent"));
      else showToast(t("siteAdmin.orders.paidNoEmail"), false);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const saveFix = async () => {
    setBusy("notes");
    try {
      const res = await adminOrdersApi.patch(order.id, {
        ...(fixName.trim() !== order.name ? { name: fixName.trim() } : {}),
        ...(fixEmail.trim().toLowerCase() !== order.email ? { email: fixEmail.trim().toLowerCase() } : {}),
      });
      if (res.data?.order) onChange(res.data.order);
      setEditing(false);
      showToast(t("siteAdmin.orders.corrected"));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const saveNotes = async () => {
    setBusy("notes");
    try {
      const res = await adminOrdersApi.patch(order.id, { paymentNote, adminNote });
      if (res.data?.order) onChange(res.data.order);
      showToast(t("siteAdmin.saved"));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const btn = "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed";
  const hasEnt = typeof order.ent_max_guests === "number";
  const appliedTitle = order.wedding_id
    ? order.wedding_title
      ? `${order.wedding_title} · /${order.wedding_slug}`
      : weddings?.find((w) => w.id === order.wedding_id)?.title ?? order.wedding_id
    : null;
  return (
    <article className="rounded-xl border border-border p-4 space-y-3" data-testid={`order-${order.number}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-mono text-sm font-semibold text-foreground">
          {order.number}
          {order.terms_version === ORDER_LEGACY_TERMS && (
            <span className="ml-2 rounded-full bg-muted px-2 py-0.5 font-sans text-[11px] font-medium text-muted-foreground" data-testid="order-legacy">
              {t("siteAdmin.orders.legacyBadge")}
            </span>
          )}
        </p>
        <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold", STATUS_STYLE[order.status])}>
          {t(`siteAdmin.orders.filter.${order.status}`)}
        </span>
      </div>
      <p className="text-sm text-foreground">
        <span translate="no" data-testid="order-package">{packageLabel(order)}</span> · {orderRangeLabel(t, order, bounds)} ·{" "}
        <strong className="tabular-nums">{formatTry(order.amount_try, lang)}</strong>
        {typeof order.list_price_try === "number" && order.amount_try < order.list_price_try && (
          <span className="ml-1 text-xs text-amber-700 dark:text-amber-400" data-testid="order-discount">
            {t("siteAdmin.orders.listPrice", { price: formatTry(order.list_price_try, lang) })}
          </span>
        )}
      </p>
      {hasEnt && (
        <p className="text-xs text-muted-foreground tabular-nums" data-testid="order-entitlement">
          {t("siteAdmin.orders.entitlement", {
            guests: order.ent_max_guests,
            media: order.ent_max_media ?? 0,
            storage: fmtBytes(order.ent_max_storage_bytes ?? 0, lang),
          })}
        </p>
      )}
      <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <div><dt className="inline text-muted-foreground">{t("siteAdmin.orders.buyer")}: </dt><dd className="inline">{order.name}</dd></div>
        <div><dt className="inline text-muted-foreground">{t("siteAdmin.orders.eventDate")}: </dt><dd className="inline tabular-nums">{order.event_date}</dd></div>
        <div><dt className="inline text-muted-foreground">{t("siteAdmin.orders.email")}: </dt><dd className="inline"><a className="underline underline-offset-2 cursor-pointer" href={`mailto:${order.email}?subject=${encodeURIComponent(order.number)}`}>{order.email}</a></dd></div>
        <div><dt className="inline text-muted-foreground">{t("siteAdmin.orders.phone")}: </dt><dd className="inline"><a className="underline underline-offset-2 cursor-pointer" href={`tel:${order.phone}`}>{order.phone}</a></dd></div>
        <div className="sm:col-span-2"><dt className="inline text-muted-foreground">{t("siteAdmin.orders.address")}: </dt><dd className="inline">{order.billing_address}{order.tax_id ? ` · ${order.tax_id}` : ""}</dd></div>
        <div className="sm:col-span-2 text-xs text-muted-foreground">
          {t("siteAdmin.orders.created", { at: order.created_at })}
          {order.paid_at ? ` · ${t("siteAdmin.orders.paidAt", { at: order.paid_at })}` : ""}
        </div>
      </dl>

      {editing && order.status === "pending" && (
        <div className="grid gap-2 rounded-xl border border-primary/30 bg-primary/5 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]" data-testid="order-fix-form">
          <input
            value={fixName}
            onChange={(e) => setFixName(e.target.value)}
            aria-label={t("siteAdmin.orders.buyer")}
            autoComplete="off"
            className="rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <input
            type="email"
            value={fixEmail}
            onChange={(e) => setFixEmail(e.target.value)}
            aria-label={t("siteAdmin.orders.email")}
            spellCheck={false}
            autoComplete="off"
            className="rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <button
            type="button"
            onClick={() => void saveFix()}
            disabled={busy !== null || (fixName.trim() === order.name && fixEmail.trim().toLowerCase() === order.email)}
            className={cn(btn, "bg-primary text-primary-foreground hover:bg-primary/90")}
          >
            <Save className="h-4 w-4" aria-hidden="true" />
            <span>{t("siteAdmin.orders.saveFix")}</span>
          </button>
        </div>
      )}

      {order.status === "paid" && (
        <div className="flex flex-col gap-1 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid="order-paid-state">
          <span>
            {order.wedding_id
              ? t("siteAdmin.orders.appliedTo", { title: appliedTitle })
              : t("siteAdmin.orders.appliedNext")}
          </span>
          {order.terms_version !== ORDER_LEGACY_TERMS && (
            <span className={cn("text-xs", order.emailed_at ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400")}>
              {order.emailed_at ? t("siteAdmin.orders.emailSentAt", { at: order.emailed_at }) : t("siteAdmin.orders.emailNotSent")}
            </span>
          )}
        </div>
      )}

      {mode && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-3" data-testid={`order-${mode}-form`}>
          {mode === "paid" && <p className="text-sm font-semibold text-foreground">{t("siteAdmin.orders.paidTitle")}</p>}
          {weddings === null ? (
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          ) : (
            <label className="block space-y-1">
              <span className="text-xs font-medium text-foreground">{t("siteAdmin.orders.applyTo")}</span>
              <select
                value={choice}
                onChange={(e) => setChoice(e.target.value)}
                disabled={foreignSlug.trim() !== ""}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">{t("siteAdmin.orders.applyNext")}</option>
                {weddings.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.title} · /{w.slug}{w.date_time ? ` · ${w.date_time.slice(0, 10)}` : ""}
                    {typeof w.guests === "number" ? ` · ${t("siteAdmin.orders.guestsShort", { n: w.guests })}` : ""}
                    {w.status === "archived" ? ` · ${t("siteAdmin.orders.archived")}` : ""}
                  </option>
                ))}
              </select>
            </label>
          )}
          {(() => {
            // Seçilen düğünde misafir sayısı hakkın üstündeyse: veri silinmez ama yeni ekleme durur.
            const w = weddings?.find((x) => x.id === choice);
            const cap = order.ent_max_guests;
            return w && typeof w.guests === "number" && typeof cap === "number" && w.guests > cap ? (
              <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400" data-testid="order-over-cap">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
                <span>{t("siteAdmin.orders.overCap", { guests: w.guests, cap })}</span>
              </p>
            ) : null;
          })()}
          <label className="block space-y-1">
            <span className="text-xs font-medium text-foreground">{t("siteAdmin.orders.foreignLabel")}</span>
            <input
              value={foreignSlug}
              onChange={(e) => setForeignSlug(e.target.value)}
              placeholder={t("siteAdmin.orders.foreignPh")}
              spellCheck={false}
              autoComplete="off"
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <span className="block text-[11px] text-muted-foreground">{t("siteAdmin.orders.foreignHint")}</span>
          </label>
          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
            <span>{t("siteAdmin.orders.applyHint")}</span>
          </p>
          {mode === "move" && order.status === "paid" && (
            <label className="block space-y-1">
              <span className="text-xs font-medium text-foreground">{t("siteAdmin.orders.moveReason")}</span>
              <input
                value={moveReason}
                onChange={(e) => setMoveReason(e.target.value)}
                maxLength={300}
                required
                autoComplete="off"
                placeholder={t("siteAdmin.orders.moveReasonPh")}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </label>
          )}
          {mode === "paid" && (
            <>
              {order.amount_try > 0 && (
                <label className="block space-y-1">
                  <span className="text-xs font-medium text-foreground">{t("siteAdmin.orders.paymentRef")}</span>
                  <input
                    value={paymentNote}
                    onChange={(e) => setPaymentNote(e.target.value)}
                    maxLength={300}
                    required
                    autoComplete="off"
                    aria-invalid={needsPaymentRef || undefined}
                    placeholder={t("siteAdmin.orders.paymentNotePh")}
                    data-testid="order-paid-ref"
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  <span className="block text-[11px] text-muted-foreground">{t("siteAdmin.orders.paymentRefHint")}</span>
                </label>
              )}
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={send}
                  onChange={(e) => setSend(e.target.checked)}
                  className="h-4 w-4 accent-primary cursor-pointer"
                />
                <span>{t("siteAdmin.orders.sendEmail")}</span>
              </label>
              {!send && (
                <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400" data-testid="order-no-email-warn">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{t("siteAdmin.orders.noEmailWarn")}</span>
                </p>
              )}
            </>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void (mode === "paid" ? confirmPaid() : confirmMove())}
              disabled={busy !== null || weddings === null || (mode === "paid" ? needsPaymentRef : order.status === "paid" && moveReason.trim().length < 3)}
              className={cn(btn, mode === "paid" ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-primary text-primary-foreground hover:bg-primary/90")}
            >
              {busy === "paid" || busy === "move" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              <span>{mode === "paid" ? t("siteAdmin.orders.confirmPaid") : t("siteAdmin.orders.apply")}</span>
            </button>
            <button type="button" onClick={() => setMode(null)} disabled={busy !== null} className={cn(btn, "border border-border hover:bg-primary/5")}>
              <span>{t("siteAdmin.orders.close")}</span>
            </button>
          </div>
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        <input
          value={paymentNote}
          onChange={(e) => setPaymentNote(e.target.value)}
          maxLength={300}
          placeholder={t("siteAdmin.orders.paymentNotePh")}
          aria-label={t("siteAdmin.orders.paymentNote")}
          className="rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
        <input
          value={adminNote}
          onChange={(e) => setAdminNote(e.target.value)}
          maxLength={1000}
          placeholder={t("siteAdmin.orders.adminNotePh")}
          aria-label={t("siteAdmin.orders.adminNote")}
          className="rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </div>
      {contract?.state === "ready" && (
        <div className="space-y-2 rounded-xl border border-border bg-muted/20 p-3 text-xs" data-testid="order-contract">
          {!contract.available ? (
            <p className="text-amber-700 dark:text-amber-400">{t("siteAdmin.ledger.unavailable")}</p>
          ) : contract.documents ? (
            <>
              <p className="font-medium text-foreground">{t("siteAdmin.ledger.contract", { hash: contract.documents.hash })}</p>
              <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-lg bg-background p-3 font-sans text-[12px] leading-relaxed">{contract.documents.text}</pre>
            </>
          ) : (
            <p className="text-muted-foreground">{t("siteAdmin.orders.noContract")}</p>
          )}
          {contract.available && <p className="text-muted-foreground">{t("siteAdmin.orders.ledgerEvents", { count: contract.events })}</p>}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void toggleContract()}
          aria-expanded={contract !== null}
          disabled={contract?.state === "loading"}
          className={cn(btn, "border border-border hover:bg-primary/5")}
        >
          {contract?.state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" aria-hidden="true" />}
          <span>{t("siteAdmin.orders.contractCopy")}</span>
        </button>
        {order.status === "pending" && (
          <button type="button" onClick={() => setEditing((v) => !v)} disabled={busy !== null} aria-expanded={editing} className={cn(btn, "border border-border hover:bg-primary/5")}>
            <Pencil className="h-4 w-4" aria-hidden="true" />
            <span>{t("siteAdmin.orders.fix")}</span>
          </button>
        )}
        {order.status === "pending" && mode !== "paid" && (
          <button type="button" onClick={() => void open("paid")} disabled={busy !== null} className={cn(btn, "bg-emerald-600 text-white hover:bg-emerald-700")}>
            <CheckCircle2 className="h-4 w-4" />
            <span>{t("siteAdmin.orders.markPaid")}</span>
          </button>
        )}
        {order.status === "paid" && mode !== "move" && (
          <button type="button" onClick={() => void open("move")} disabled={busy !== null} className={cn(btn, "border border-border hover:bg-primary/5")}>
            <ArrowRightLeft className="h-4 w-4" />
            <span>{t("siteAdmin.orders.move")}</span>
          </button>
        )}
        {/* Geçiş siparişinde satış/sözleşme yok → "ödendi" e-postası gönderilmez. */}
        {order.status === "paid" && order.terms_version !== ORDER_LEGACY_TERMS && (
          <button type="button" onClick={() => void sendEmail()} disabled={busy !== null} className={cn(btn, "border border-border hover:bg-primary/5")}>
            {busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            <span>{order.emailed_at ? t("siteAdmin.orders.resendEmail") : t("siteAdmin.orders.sendEmailNow")}</span>
          </button>
        )}
        {order.status !== "cancelled" && (
          <button type="button" onClick={() => void cancel()} disabled={busy !== null} className={cn(btn, "border border-border hover:bg-red-500/5 hover:text-red-600")}>
            {busy === "cancelled" ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
            <span>{t("siteAdmin.orders.cancel")}</span>
          </button>
        )}
        <button type="button" onClick={() => void saveNotes()} disabled={busy !== null || !notesDirty} className={cn(btn, "border border-border hover:bg-primary/5")}>
          {busy === "notes" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          <span>{t("siteAdmin.orders.saveNotes")}</span>
        </button>
      </div>
    </article>
  );
}

/**
 * Teklifle (elle) sipariş — teklif → satış HESAP PLANI değiştirmez (2026-10-15). Paket = admin
 * Paketler'deki bir TANIM (Pro, Max ya da özel/kurumsal): misafir dahil TÜM sayılar tanımdan
 * (2026-10-16 — aralık / elle misafir sınırı yok). Tutar varsayılan liste fiyatı; altındaysa
 * (indirim) ya da paketin fiyatı yoksa gerekçe zorunlu (sunucu da denetler, deftere yazılır).
 */
type OrganizerHit = { id: string; email: string; display_name: string | null; phone_e164: string | null; wedding_count: number };

/**
 * Manuel siparişte MEVCUT organizatörü seç (2026-10-16): e-posta / ad ile arar (`GET /api/admin/users?q=`).
 * Seçilince ad, e-posta, telefon hesaptan dolar ve e-posta kilitlenir — ödenince paket bu hesabın
 * Paketlerim'ine düşer (siparişler hesaba e-postayla bağlanır).
 */
function OrganizerPicker({ picked, onPick, onClear }: {
  picked: OrganizerHit | null;
  onPick: (o: OrganizerHit) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<OrganizerHit[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setHits(null);
      setFailed(false);
      return;
    }
    let alive = true;
    const timer = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await adminUsersApi.get<OrganizerHit[]>(`?q=${encodeURIComponent(term)}&limit=8`);
        if (alive) {
          setHits(res.data ?? []);
          setFailed(false);
        }
      } catch {
        if (alive) setFailed(true);
      } finally {
        if (alive) setBusy(false);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [q]);

  if (picked) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-background px-3 py-2 text-xs" data-testid="organizer-picked">
        <UserCheck className="h-4 w-4 text-primary" aria-hidden="true" />
        <span className="font-medium">{t("siteAdmin.orders.pickedLabel", { name: picked.display_name ?? picked.email, email: picked.email })}</span>
        <span className="text-muted-foreground">{t("siteAdmin.orders.pickWeddings", { count: picked.wedding_count })}</span>
        <button type="button" onClick={onClear} className="ml-auto font-semibold text-primary hover:underline cursor-pointer">
          {t("siteAdmin.orders.pickChange")}
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-1.5" data-testid="organizer-picker">
      <label className="block space-y-1 text-xs font-medium text-foreground">
        <span>{t("siteAdmin.orders.pickOrganizer")}</span>
        <span className="relative block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("siteAdmin.orders.pickOrganizerPh")}
            autoComplete="off"
            spellCheck={false}
            className="w-full rounded-xl border border-border bg-background py-2 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          {busy && <Loader2 className="absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" aria-hidden="true" />}
        </span>
      </label>
      {failed ? (
        <p className="text-xs text-red-500">{t("siteAdmin.orders.pickFailed")}</p>
      ) : hits && hits.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("siteAdmin.orders.pickNone")}</p>
      ) : hits ? (
        <ul className="divide-y divide-border rounded-xl border border-border bg-background" data-testid="organizer-hits">
          {hits.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                onClick={() => {
                  onPick(o);
                  setQ("");
                }}
                className="flex w-full flex-wrap items-center gap-x-2 px-3 py-2 text-left text-xs hover:bg-primary/5 cursor-pointer"
              >
                <span className="font-medium">{o.display_name ?? o.email}</span>
                <span className="text-muted-foreground">{o.email}</span>
                <span className="ml-auto text-muted-foreground">{t("siteAdmin.orders.pickWeddings", { count: o.wedding_count })}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">{t("siteAdmin.orders.pickHint")}</p>
      )}
    </div>
  );
}

type SellablePackage = { slug: string; label: string; price: number | null; guests: number };
type PlanApiRow = { slug: string; label: string; price_try?: number | null; max_guests_per_wedding: number; is_active: number; is_default: number };

function ManualOrderForm({ onCreated, showToast }: {
  onCreated: () => void;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const [f, setF] = useState({
    name: "", email: "", phone: "", eventDate: "", package: "",
    amount: "", billingAddress: "", adminNote: "", quantity: "1",
  });
  const [saving, setSaving] = useState(false);
  const [picked, setPicked] = useState<OrganizerHit | null>(null);
  // Satılabilir paket tanımları (aktif, varsayılan/Free değil) — sayılar ve liste fiyatı buradan.
  const [packages, setPackages] = useState<SellablePackage[] | null>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await adminPlansApi.get<PlanApiRow[]>("/");
        const list = (res.data ?? [])
          .filter((p) => p.is_active && !p.is_default)
          .map((p) => ({ slug: p.slug, label: p.label, price: typeof p.price_try === "number" ? p.price_try : null, guests: p.max_guests_per_wedding }));
        if (!alive) return;
        setPackages(list);
        const first = list[0];
        if (first) setF((p) => (p.package ? p : { ...p, package: first.slug, amount: first.price === null ? "" : String(first.price) }));
      } catch {
        if (alive) setPackages([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  const pkg = packages?.find((p) => p.slug === f.package) ?? null;
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF((p) => ({ ...p, [k]: e.target.value }));
  const choosePackage = (slug: string) => {
    const next = packages?.find((p) => p.slug === slug);
    setF((p) => ({ ...p, package: slug, amount: next && next.price !== null ? String(next.price) : "" }));
  };
  const pick = (o: OrganizerHit) => {
    setPicked(o);
    setF((p) => ({ ...p, name: o.display_name?.trim() || o.email, email: o.email, phone: o.phone_e164 ?? "" }));
  };
  const amount = Number(f.amount.replace(/\./g, ""));
  // Liste fiyatının altı (indirim / 0 ₺) ya da fiyatı olmayan paket → gerekçe zorunlu.
  const discounted = pkg !== null && (pkg.price === null || amount < pkg.price);
  const body = {
    name: f.name.trim(), email: f.email.trim().toLowerCase(), phone: f.phone.trim(), eventDate: f.eventDate,
    package: f.package, amount,
    billingAddress: f.billingAddress.trim(), adminNote: f.adminNote.trim(),
    // "3 Pro paket" → 3 ayrı sipariş (her biri tek düğün, kendi numarası); tutar paket başına.
    quantity: Number(f.quantity || "1"),
  };
  const reasonMissing = discounted && body.adminNote.length < 3;
  const valid = adminOrderCreateSchema.safeParse(body).success && f.amount.trim() !== "" && pkg !== null && !reasonMissing;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) {
      showToast(t(reasonMissing ? "siteAdmin.orders.errors.reason" : "siteAdmin.invalid"), false);
      return;
    }
    setSaving(true);
    try {
      const res = await adminOrdersApi.create(body);
      const created = res.data?.orders ?? (res.data?.order ? [res.data.order] : []);
      showToast(
        created.length > 1
          ? t("siteAdmin.orders.manualCreatedMany", { count: created.length, numbers: created.map((o) => o.number).join(", ") })
          : t("siteAdmin.orders.manualCreated", { number: created[0]?.number ?? "" }),
      );
      onCreated();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast(ERROR_KEYS[msg] ? t(ERROR_KEYS[msg]) : t("siteAdmin.saveFailed", { error: msg }), false);
    } finally {
      setSaving(false);
    }
  };

  const input = "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20";
  const label = "block space-y-1 text-xs font-medium text-foreground";
  return (
    <form onSubmit={(e) => void submit(e)} className="border-b border-border bg-primary/5 px-5 py-4 space-y-3" data-testid="manual-order-form">
      <p className="text-xs text-muted-foreground">{t("siteAdmin.orders.manualHint")}</p>
      <OrganizerPicker picked={picked} onPick={pick} onClear={() => setPicked(null)} />
      <div className="grid gap-3 sm:grid-cols-2">
        <label className={label}><span>{t("siteAdmin.orders.buyer")}</span><input value={f.name} onChange={set("name")} required autoComplete="off" className={input} /></label>
        <label className={label}>
          <span>{t("siteAdmin.orders.email")}</span>
          <input
            type="email"
            value={f.email}
            onChange={set("email")}
            readOnly={!!picked}
            required
            spellCheck={false}
            autoComplete="off"
            className={cn(input, picked && "bg-muted/50 text-muted-foreground")}
          />
        </label>
        <label className={label}><span>{t("siteAdmin.orders.phone")}</span><input type="tel" value={f.phone} onChange={set("phone")} autoComplete="off" className={input} /></label>
        <label className={label}><span>{t("siteAdmin.orders.eventDate")}</span><input type="date" value={f.eventDate} onChange={set("eventDate")} required className={input} /></label>
        <label className={cn(label, "sm:col-span-2")}>
          <span>{t("siteAdmin.orders.manualPlan")}</span>
          <select
            value={f.package}
            onChange={(e) => choosePackage(e.target.value)}
            disabled={!packages?.length}
            data-testid="manual-package"
            className={cn(input, "cursor-pointer")}
          >
            {packages?.map((p) => (
              <option key={p.slug} value={p.slug}>
                {t("siteAdmin.orders.packageOption", {
                  label: p.label,
                  guests: new Intl.NumberFormat(i18n.language).format(p.guests),
                  price: p.price === null ? t("landing.plans.priceQuote") : formatTry(p.price, i18n.language),
                })}
              </option>
            ))}
          </select>
          {packages !== null && packages.length === 0 && (
            <span className="block text-[11px] text-red-500">{t("siteAdmin.orders.noPackages")}</span>
          )}
        </label>
        <label className={label}>
          <span>{t("siteAdmin.orders.manualQuantity")}</span>
          <input type="number" min={1} max={20} value={f.quantity} onChange={set("quantity")} required className={input} />
        </label>
        <label className={label}>
          <span>{Number(f.quantity) > 1 ? t("siteAdmin.orders.manualAmountEach") : t("siteAdmin.orders.manualAmount")}</span>
          <input inputMode="numeric" value={f.amount} onChange={set("amount")} required className={input} />
        </label>
        {pkg && (
          <p className="sm:col-span-2 text-xs text-muted-foreground" data-testid="manual-list-price">
            {pkg.price === null
              ? t("siteAdmin.orders.noListPrice")
              : discounted
                ? t("siteAdmin.orders.discountNote", { price: formatTry(pkg.price, i18n.language), discount: formatTry(Math.max(0, pkg.price - (Number.isFinite(amount) ? amount : 0)), i18n.language) })
                : t("siteAdmin.orders.listPriceNote", { price: formatTry(pkg.price, i18n.language) })}
          </p>
        )}
        <label className={cn(label, "sm:col-span-2")}><span>{t("siteAdmin.orders.address")}</span><input value={f.billingAddress} onChange={set("billingAddress")} autoComplete="off" className={input} /></label>
        <label className={cn(label, "sm:col-span-2")}>
          <span>{discounted ? t("siteAdmin.orders.reasonRequired") : t("siteAdmin.orders.adminNote")}</span>
          <input
            value={f.adminNote}
            onChange={set("adminNote")}
            autoComplete="off"
            required={discounted}
            aria-invalid={reasonMissing || undefined}
            data-testid="manual-reason"
            className={input}
          />
        </label>
      </div>
      {Number(f.quantity) > 1 && (
        <p data-testid="manual-quantity-hint" className="text-xs text-muted-foreground">
          {t("siteAdmin.orders.manualQuantityHint", { count: Number(f.quantity) })}
        </p>
      )}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving || !valid}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Plus className="h-4 w-4" aria-hidden="true" />}
          <span>{t("siteAdmin.orders.manualCreate")}</span>
        </button>
      </div>
    </form>
  );
}
